import { type FC } from "react";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import useUserDataProvider from "src/contexts/snug/userData-context";
import { useRouter } from "src/hooks/use-router";
import { paths } from "src/paths";

/**
 * Large red banner shown across the dashboard when the professional group's
 * direct-charge account standing is in arrears (WARNING or LOCKED). New clients
 * are paused in these states. Admins get a CTA to add a payment method; agents
 * are told to contact their account admin (they can't resolve billing).
 */
export const DirectChargeArrearsBanner: FC = () => {
  const { professionalGroup, isFirmAdmin } = useUserDataProvider();
  const router = useRouter();

  const status = professionalGroup?.direct_charge_status;
  if (status !== "WARNING" && status !== "LOCKED") {
    return null;
  }

  const isLocked = status === "LOCKED";
  const contactAdmin = " Please contact your account admin to resolve this.";

  const title = isLocked
    ? "Your account is locked due to a past-due payment"
    : "Your account has a past-due payment";

  const message = isLocked
    ? "Adding and editing clients and changing account settings are paused until a valid payment method is on file and all failed payments are resolved."
    : "Adding new clients is paused until a valid payment method is on file and all failed payments are resolved.";

  return (
    <Box sx={{ px: { xs: 2, sm: 3 }, pt: 2 }}>
      <Alert
        severity="error"
        variant="filled"
        action={
          isFirmAdmin ? (
            <Button
              color="inherit"
              variant="outlined"
              size="small"
              onClick={() =>
                router.push(paths.dashboard.accountTab("billing"))
              }
              sx={{ whiteSpace: "nowrap" }}
            >
              Add payment method
            </Button>
          ) : undefined
        }
      >
        <AlertTitle sx={{ fontWeight: 700 }}>{title}</AlertTitle>
        {isFirmAdmin ? message : message + contactAdmin}
      </Alert>
    </Box>
  );
};
